import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import adminApi from "@/lib/adminApi";
import {
  AlertCircle, ArrowLeft, ChevronDown, ChevronRight,
  Clipboard, Loader2, Trash2, ExternalLink, CheckCircle2, XCircle,
} from "lucide-react";

const STATUS_OPTIONS = [
  "New", "Contacted", "Qualified", "Sample Requested", "Sample Sent",
  "Trial", "Quotation", "Won", "Lost", "Spam",
];

const TYPE_BADGE = {
  SAMPLE: "bg-blue-50 text-blue-800 border-blue-200",
  DIRECT: "bg-purple-50 text-purple-800 border-purple-200",
  DISTRIBUTOR: "bg-teal-50 text-teal-800 border-teal-200",
  TECHNICAL_ENQUIRY: "bg-amber-50 text-amber-800 border-amber-200",
  GENERAL_ENQUIRY: "bg-slate-50 text-slate-700 border-slate-200",
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

function humanKey(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function whatsappHref(phone) {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, "");
  if (digits.length < 6) return null;
  return `https://wa.me/${digits}`;
}

const Section = ({ title, children, testid }) => (
  <section className="bg-white border border-[var(--border)] rounded-[4px] p-5 mb-4" data-testid={testid}>
    <h2 className="text-xs uppercase tracking-[0.18em] text-[var(--pharma-blue)] font-semibold mb-4">{title}</h2>
    {children}
  </section>
);

const KV = ({ label, children, testid }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 border-b border-[var(--border)] last:border-b-0" data-testid={testid}>
    <div className="text-xs text-[var(--slate)] uppercase tracking-wider pt-0.5">{label}</div>
    <div className="text-sm text-[var(--charcoal)] break-words">{children}</div>
  </div>
);

const StatusBadge = ({ label, state, error }) => {
  const map = {
    sent: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", text: "Sent" },
    failed: { cls: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500", text: "Failed" },
    mocked: { cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500", text: "Mocked" },
  };
  const s = map[state] || map.mocked;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 border rounded-[2px] ${s.cls}`}
      title={error || undefined}
      data-testid={`email-badge-${state}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {label ? `${label}: ${s.text}` : s.text}
    </span>
  );
};

const AdminLeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesSavedAt, setNotesSavedAt] = useState(null);
  const [owner, setOwner] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState("");
  const [emails, setEmails] = useState([]);
  const [copyOk, setCopyOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get(`/leads/${id}`);
      setLead(data);
      setNotes(data.notes || "");
      setOwner(data.owner || "");
      setFollowUp(data.next_follow_up ? data.next_follow_up.slice(0, 10) : "");
      const em = await adminApi.get(`/email-notifications`, { params: { lead_id: id } });
      setEmails(em.data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load lead");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2500);
  };

  const saveNotes = async () => {
    if ((lead?.notes || "") === notes) return;
    setSavingNotes(true);
    try {
      const { data } = await adminApi.patch(`/leads/${id}`, { notes });
      setLead(data);
      setNotesSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const saveOwner = async () => {
    if ((lead?.owner || "") === (owner || "")) return;
    try {
      const { data } = await adminApi.patch(`/leads/${id}`, { owner: owner || null });
      setLead(data);
      flash("Owner updated");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save owner");
    }
  };

  const saveFollowUp = async (value) => {
    setFollowUp(value);
    try {
      const iso = value ? new Date(value + "T00:00:00Z").toISOString() : null;
      const { data } = await adminApi.patch(`/leads/${id}`, { next_follow_up: iso });
      setLead(data);
      flash("Follow-up updated");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save follow-up");
    }
  };

  const changeStatus = async (newStatus) => {
    if (!newStatus || newStatus === lead.status) return;
    setStatusBusy(true);
    try {
      const { data } = await adminApi.patch(`/leads/${id}/status`, { status: newStatus });
      setLead(data);
      flash(`Status changed to "${newStatus}"`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to change status");
    } finally {
      setStatusBusy(false);
    }
  };

  const doDelete = async () => {
    try {
      await adminApi.delete(`/leads/${id}`);
      navigate("/admin", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete lead");
    }
  };

  const copyContact = async () => {
    if (!lead) return;
    const c = lead.contact || {};
    const lines = [
      c.name && `Name: ${c.name}`,
      c.position && `Position: ${c.position}`,
      c.company && `Company: ${c.company}`,
      c.email && `Email: ${c.email}`,
      c.phone && `Phone: ${c.phone}`,
      c.country && `Country: ${c.country}`,
      c.city && `City: ${c.city}`,
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      setCopyOk(true);
      window.setTimeout(() => setCopyOk(false), 1500);
    } catch {
      flash("Copy failed — clipboard blocked");
    }
  };

  const derivedSource = useMemo(() => {
    if (!lead) return null;
    const a = lead.attribution || {};
    if (a.product_slug) return { label: "Product", value: a.product_slug, href: `/products/${a.product_slug}` };
    if (a.solution_slug) return { label: "Solution", value: a.solution_slug, href: `/solutions/${a.solution_slug}` };
    if (a.industry) return { label: "Industry", value: a.industry, href: `/industries/${a.industry}` };
    if (a.resource) return { label: "Resource", value: a.resource };
    return null;
  }, [lead]);

  if (loading) {
    return (
      <AdminLayout title="Lead">
        <div className="p-12 text-center text-sm text-[var(--slate)] flex items-center justify-center gap-2" data-testid="lead-loading">
          <Loader2 size={16} className="animate-spin" /> Loading lead…
        </div>
      </AdminLayout>
    );
  }
  if (error || !lead) {
    return (
      <AdminLayout title="Lead">
        <div className="p-6 border border-red-200 bg-red-50 text-sm text-red-700 rounded-[4px]" data-testid="lead-error">
          {error || "Lead not found"}
        </div>
        <div className="mt-4">
          <Link to="/admin" className="text-sm text-[var(--pharma-blue)] inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to leads</Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Lead">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[var(--navy)] text-white text-xs px-4 py-2 rounded-[3px] shadow" data-testid="admin-toast">
          {toast}
        </div>
      )}

      {/* Breadcrumb */}
      <nav className="mb-4 text-xs text-[var(--slate)] flex items-center gap-2" aria-label="Breadcrumb" data-testid="admin-breadcrumb">
        <Link to="/admin" className="hover:text-[var(--navy)]">Admin</Link>
        <ChevronRight size={12} />
        <Link to="/admin" className="hover:text-[var(--navy)]">Leads</Link>
        <ChevronRight size={12} />
        <span className="text-[var(--charcoal)] font-mono">{id.slice(0, 8)}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* Left column */}
        <div className="min-w-0">
          {/* Header */}
          <div className="bg-white border border-[var(--border)] rounded-[4px] p-5 mb-4" data-testid="lead-header">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs text-[var(--slate)]">{fmtDT(lead.created_at)}</div>
                <h1 className="mt-1 font-heading text-2xl font-semibold text-[var(--navy)] tracking-tight">
                  {lead.contact?.company || "—"}
                </h1>
                <div className="text-sm text-[var(--charcoal)] mt-1">
                  {lead.contact?.name}
                  {lead.contact?.position && <span className="text-[var(--slate)]"> · {lead.contact.position}</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-[2px] ${TYPE_BADGE[lead.type] || "border-[var(--border)]"}`}>
                  {lead.type?.replace("_ENQUIRY", "")}
                </span>
                <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-[2px] bg-[var(--bg-alt)] border-[var(--border)] text-[var(--navy)]">
                  {lead.status}
                </span>
                {lead.deleted_at && (
                  <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-[2px] bg-red-50 border-red-200 text-red-700">
                    Deleted
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact */}
          <Section title="Contact" testid="lead-section-contact">
            <KV label="Name">{formatValue(lead.contact?.name)}</KV>
            <KV label="Position">{formatValue(lead.contact?.position)}</KV>
            <KV label="Company">{formatValue(lead.contact?.company)}</KV>
            <KV label="Email">
              {lead.contact?.email ? (
                <a href={`mailto:${lead.contact.email}`} className="text-[var(--pharma-blue)] hover:underline">{lead.contact.email}</a>
              ) : "—"}
            </KV>
            <KV label="Phone">
              {lead.contact?.phone ? (
                <span className="inline-flex items-center gap-3">
                  <a href={`tel:${lead.contact.phone}`} className="text-[var(--pharma-blue)] hover:underline">{lead.contact.phone}</a>
                  {whatsappHref(lead.contact.phone) && (
                    <a href={whatsappHref(lead.contact.phone)} target="_blank" rel="noreferrer" className="text-xs text-[var(--slate)] underline underline-offset-2">WhatsApp</a>
                  )}
                </span>
              ) : "—"}
            </KV>
            <KV label="Country">{formatValue(lead.contact?.country)}</KV>
            <KV label="City">{formatValue(lead.contact?.city)}</KV>
            <KV label="Preferred contact">{formatValue(lead.contact?.preferred_contact_method)}</KV>
          </Section>

          {/* Attribution */}
          <Section title="Attribution" testid="lead-section-attribution">
            <KV label="Source page">
              {lead.source_page ? (
                <Link to={lead.source_page} className="text-[var(--pharma-blue)] hover:underline inline-flex items-center gap-1">
                  {lead.source_page} <ExternalLink size={11} />
                </Link>
              ) : "—"}
            </KV>
            <KV label="Source of interest">
              {derivedSource ? (
                derivedSource.href ? (
                  <Link to={derivedSource.href} className="text-[var(--pharma-blue)] hover:underline">
                    {derivedSource.label}: {derivedSource.value}
                  </Link>
                ) : (
                  <span>{derivedSource.label}: {derivedSource.value}</span>
                )
              ) : "—"}
            </KV>
            <KV label="Referrer">{formatValue(lead.attribution?.referrer)}</KV>
          </Section>

          {/* Payload */}
          <Section title="Submitted details" testid="lead-section-payload">
            {Object.keys(lead.payload || {}).length === 0 ? (
              <div className="text-sm text-[var(--slate)]">No submitted details.</div>
            ) : (
              Object.entries(lead.payload).map(([k, v]) => (
                <KV key={k} label={humanKey(k)} testid={`payload-${k}`}>{formatValue(v)}</KV>
              ))
            )}
          </Section>

          {/* Attachments — surfaced from any payload key ending in _id (uploads) */}
          {Object.entries(lead.payload || {}).some(([k, v]) => k.endsWith("_id") && v) && (
            <Section title="Attachments" testid="lead-section-attachments">
              <ul className="space-y-2 text-sm">
                {Object.entries(lead.payload)
                  .filter(([k, v]) => k.endsWith("_id") && v)
                  .map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between border border-[var(--border)] rounded-[3px] px-3 py-2" data-testid={`attachment-${k}`}>
                      <span className="text-[var(--charcoal)]">{humanKey(k.replace(/_id$/, ""))}</span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${v}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--pharma-blue)] hover:underline text-xs inline-flex items-center gap-1"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await adminApi.get(`/uploads/${v}`, { responseType: "blob", baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
                            const blobUrl = URL.createObjectURL(res.data);
                            const a = document.createElement("a");
                            a.href = blobUrl;
                            a.target = "_blank";
                            a.rel = "noreferrer";
                            a.click();
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                          } catch {
                            flash("Download failed");
                          }
                        }}
                      >
                        Download <ExternalLink size={11} />
                      </a>
                    </li>
                  ))}
              </ul>
            </Section>
          )}

          {/* Meta */}
          <Section title="Metadata" testid="lead-section-meta">
            <KV label="User agent"><span className="text-xs font-mono text-[var(--slate)] break-all">{formatValue(lead.meta?.user_agent)}</span></KV>
            <KV label="IP">{formatValue(lead.meta?.ip)}</KV>
            <KV label="Submitted at (client)">{formatValue(lead.meta?.submitted_at_client)}</KV>
            <KV label="Honeypot triggered">
              {lead.meta?.honeypot_triggered ? (
                <span className="inline-flex items-center gap-1 text-red-700"><AlertCircle size={14} /> Yes — treat as spam</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[var(--slate)]"><CheckCircle2 size={14} /> No</span>
              )}
            </KV>
            <KV label="Consent acknowledged">
              {lead.meta?.consent_ack
                ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={14} /> Yes</span>
                : <span className="inline-flex items-center gap-1 text-[var(--slate)]"><XCircle size={14} /> No</span>}
            </KV>
          </Section>

          {/* Notes */}
          <Section title="Notes" testid="lead-section-notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Internal notes (auto-saves on blur)"
              rows={5}
              className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] focus:outline-none focus:border-[var(--pharma-blue)] font-mono"
              data-testid="lead-notes-input"
            />
            <div className="mt-2 text-xs text-[var(--slate)]">
              {savingNotes ? "Saving…" : notesSavedAt ? `Saved at ${notesSavedAt.toLocaleTimeString()}` : "Auto-saves on blur"}
            </div>
          </Section>

          {/* Status history */}
          <Section title="Status history" testid="lead-section-history">
            <ol className="relative border-l border-[var(--border)] ml-2 pl-4 space-y-3" data-testid="lead-status-history">
              {(lead.status_history || []).map((h, i) => (
                <li key={i} className="text-sm" data-testid={`history-${i}`}>
                  <div className="absolute -left-[5px] w-2.5 h-2.5 bg-[var(--pharma-blue)] rounded-full mt-1" />
                  <div className="text-[var(--charcoal)] font-medium">{h.status}</div>
                  <div className="text-xs text-[var(--slate)]">
                    {fmtDT(h.at)} · by {h.by || "system"}
                  </div>
                </li>
              ))}
            </ol>
          </Section>

          {/* Email notifications */}
          <Section title="Email notifications" testid="lead-section-emails">
            {lead.email_delivery && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="email-delivery-summary">
                <StatusBadge label="Internal" state={
                  lead.email_delivery.mode === "MOCKED" ? "mocked"
                  : lead.email_delivery.internal?.sent ? "sent" : "failed"
                } error={lead.email_delivery.internal?.error} />
                {lead.email_delivery.customer?.to && (
                  <StatusBadge label="Customer" state={
                    lead.email_delivery.mode === "MOCKED" ? "mocked"
                    : lead.email_delivery.customer?.sent ? "sent" : "failed"
                  } error={lead.email_delivery.customer?.error} />
                )}
                <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-[var(--slate)] px-2 py-1">
                  Provider: {lead.email_delivery.mode === "MOCKED" ? "mocked" : "Resend"}
                </span>
              </div>
            )}
            {emails.length === 0 ? (
              <div className="text-sm text-[var(--slate)]">No email records found for this lead.</div>
            ) : (
              <ul className="space-y-3">
                {emails.map((e, i) => (
                  <li key={i} className="border border-[var(--border)] rounded-[3px] p-3 text-sm" data-testid={`email-${i}`}>
                    <div className="flex justify-between text-xs text-[var(--slate)]">
                      <span>To: <span className="text-[var(--charcoal)] font-medium">{e.to}</span></span>
                      <span>{fmtDT(e.created_at)}</span>
                    </div>
                    <div className="text-[var(--navy)] font-medium mt-1">{e.subject}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <StatusBadge state={
                        e.status === "sent" ? "sent"
                        : e.status === "MOCKED" ? "mocked"
                        : "failed"
                      } />
                      {e.kind && (
                        <span className="text-[10px] uppercase tracking-wider text-[var(--slate)] px-2 py-0.5 border border-[var(--border)] rounded-[2px]">
                          {e.kind}
                        </span>
                      )}
                      {e.provider_message_id && (
                        <span className="text-[10px] text-[var(--slate)] font-mono">id: {e.provider_message_id}</span>
                      )}
                    </div>
                    {e.error && (
                      <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-[3px] p-2" data-testid={`email-${i}-error`}>
                        {e.error}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Right column — actions sidebar */}
        <aside className="lg:sticky lg:top-[68px] self-start space-y-4">
          <div className="bg-white border border-[var(--border)] rounded-[4px] p-5" data-testid="lead-actions">
            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Status</label>
              <select
                value={lead.status}
                onChange={(e) => changeStatus(e.target.value)}
                disabled={statusBusy}
                className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
                data-testid="lead-status-select"
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Owner</label>
              <input
                type="text"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                onBlur={saveOwner}
                placeholder="Assign owner (e.g. Ali)"
                className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] focus:outline-none focus:border-[var(--pharma-blue)]"
                data-testid="lead-owner-input"
              />
            </div>

            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Next Follow-Up</label>
              <input
                type="date"
                value={followUp}
                onChange={(e) => saveFollowUp(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] focus:outline-none focus:border-[var(--pharma-blue)]"
                data-testid="lead-followup-input"
              />
            </div>

            <button
              type="button"
              onClick={copyContact}
              className="btn-outline w-full text-xs inline-flex items-center justify-center gap-2 !py-2"
              data-testid="lead-copy-button"
            >
              <Clipboard size={14} /> {copyOk ? "Copied!" : "Copy contact info"}
            </button>

            <div className="mt-4 border-t border-[var(--border)] pt-3">
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="text-xs text-[var(--slate)] inline-flex items-center gap-1 hover:text-[var(--navy)]"
                data-testid="lead-advanced-toggle"
              >
                <ChevronDown size={12} className={`transition-transform ${advancedOpen ? "rotate-0" : "-rotate-90"}`} />
                Advanced
              </button>
              {advancedOpen && (
                <div className="mt-3">
                  {!confirmDelete ? (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="w-full text-xs text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 rounded-[3px] py-2 inline-flex items-center justify-center gap-2"
                      data-testid="lead-delete-button"
                    >
                      <Trash2 size={14} /> Delete lead
                    </button>
                  ) : (
                    <div className="border border-red-200 bg-red-50 rounded-[3px] p-3 text-xs text-red-700 space-y-2" data-testid="lead-delete-confirm">
                      <div>This will soft-delete the lead. Continue?</div>
                      <div className="flex gap-2">
                        <button onClick={doDelete} className="btn-primary !py-1 !px-3 text-[11px]" data-testid="lead-delete-confirm-yes">Confirm delete</button>
                        <button onClick={() => setConfirmDelete(false)} className="btn-outline !py-1 !px-3 text-[11px]" data-testid="lead-delete-confirm-no">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <Link to="/admin" className="text-xs text-[var(--slate)] inline-flex items-center gap-1 hover:text-[var(--navy)]" data-testid="lead-back-link">
            <ArrowLeft size={12} /> Back to leads
          </Link>
        </aside>
      </div>
    </AdminLayout>
  );
};

export default AdminLeadDetail;
